import zipWith from 'lodash/fp/zipWith';
import Notifications from 'react-notification-system-redux';
import { api } from '../../../general/apiScenarios';
import { UserAccessRight } from '../../../general/model';
import { ListenerApiLight, _select, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { getUsers } from '../app/selectors';
import * as permissionModalActions from './actions';
import { Messages } from './model';
import { getPermissionButtonRelatedNode, getPermissions, getSelectedsUsers } from './selectors';

export function permissionModalRootListener() {
  watchModalCloseEvents();
  watchModalOpened();
  watchApplyButtonClicked();
}

export const watchModalCloseEvents = function () {
  lm.takeEvery(
    [permissionModalActions.modalCloseUiEvent, permissionModalActions.closeButtonClicked],
    function (_, lapi) {
      lapi.dispatch(permissionModalActions.modalClosed());
    },
  );
};

export const watchModalOpened = function () {
  lm.takeLatest(permissionModalActions.modalOpened, function (action, lapi) {
    lapi.dispatch(permissionModalActions.loadingChanged({ isLoading: true }));

    if (lapi.signal.aborted) return;
    lapi.dispatch(permissionModalActions.loadingChanged({ isLoading: false }));
  });
};

export function watchApplyButtonClicked() {
  lm.takeEvery(permissionModalActions.applyButtonClicked, async function (action, lapi) {
    lapi.dispatch(permissionModalActions.applyBulkPermissionsStarted());
    const users = _select(lapi, getSelectedsUsers);
    const allUsers = _select(lapi, getUsers) ?? [];
    const node = _select(lapi, getPermissionButtonRelatedNode);
    if (!node) {
      return;
    }

    const permissionsRecord: Record<UserAccessRight, boolean> = _select(lapi, getPermissions);
    const permissions = (Object.keys(UserAccessRight) as UserAccessRight[]).filter(
      (permission) => permissionsRecord[permission],
    );
    const results = await Promise.all(
      users.map((userID) => applyPermissions(lapi, userID, node.id, permissions)),
    );
    if (lapi.signal.aborted) return;
    const pairs = zipWith((userId, result) => ({
      user: allUsers.find((user) => user.id === userId),
      result,
    }))(users, results);
    const badUsers = pairs
      .filter(({ result }) => result === null)
      .map(({ user }) => user)
      .filter((user) => user !== undefined);
    if (badUsers.length) {
      lapi.dispatch(
        Notifications.error(Messages.applyPermissionsError(badUsers.map(({ id }) => String(id)))),
      );
    } else {
      lapi.dispatch(permissionModalActions.modalClosed());
    }

    if (pairs.some(({ result }) => result)) {
      lapi.dispatch(Notifications.info(Messages.settingPermissions()));
    }

    lapi.dispatch(permissionModalActions.applyBulkPermissionsFinished());
  });
}

export async function applyPermissions(
  lapi: ListenerApiLight,
  userID: number,
  nodeID: number,
  permissions: UserAccessRight[],
) {
  lapi.dispatch(permissionModalActions.applyPermissionsStarted());

  const result = await api.fs.permissions.post(userID, nodeID, permissions, 'chub');
  if (lapi.signal.aborted) return;
  if (result.success) {
    logger.lapi(lapi, {
      action: 'File',
      sub_action: 'Permissions',
      extra: {
        int_1: nodeID,
        // permissions
      },
    });
    lapi.dispatch(permissionModalActions.applyPermissionsSuccess({ result }));
    return result.data.response;
  } else {
    const errorMessage = result.errorData?.response?.data ?? "Can't set permissions";
    lapi.dispatch(permissionModalActions.applyPermissionsError({ error: errorMessage }));

    if (errorMessage.includes('Can not set access to child node if access exists to parent node')) {
      lapi.dispatch(
        Notifications.error({
          title: 'To grant access, you must select the initial folder.',
          message: 'Contact your administrator or support chat if the problem still presist.',
        }),
      );
    } else {
      lapi.dispatch(Notifications.error({ title: 'Permissions Error', message: errorMessage }));
    }
    return null;
  }
}
